import { ChainId } from 'constants/chain'
import { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useActiveWeb3React } from '../../hooks'
import { AppDispatch, AppState } from '../index'
import {
  addPopup,
  ApplicationModal,
  PopupContent,
  removePopup,
  setOpenModal,
  setDaoInfoLeftSidedOpenStatus
} from './actions'

export function useBlockNumber(chainId?: ChainId): number | undefined {
  const { chainId: curChainId } = useActiveWeb3React()

  return useSelector((state: AppState) => state.application.blockNumber[chainId || curChainId || -1])
}

export function useModalOpen(modal: ApplicationModal): boolean {
  const openModal = useSelector((state: AppState) => state.application.openModal)
  return openModal === modal
}

export function useToggleModal(modal: ApplicationModal): () => void {
  const open = useModalOpen(modal)
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(() => dispatch(setOpenModal(open ? null : modal)), [dispatch, modal, open])
}

export function useOpenModal(modal: ApplicationModal): () => void {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(() => dispatch(setOpenModal(modal)), [dispatch, modal])
}

export function useCloseModals(): () => void {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback(() => dispatch(setOpenModal(null)), [dispatch])
}

export function useWalletModalToggle(): () => void {
  return useToggleModal(ApplicationModal.WALLET)
}

export function useSettingsModalToggle(): () => void {
  return useToggleModal(ApplicationModal.SETTINGS)
}

export function useVoteModalToggle(): () => void {
  return useToggleModal(ApplicationModal.VOTE)
}

export function useSignLoginModalControl() {
  const dispatch = useDispatch<AppDispatch>()
  return {
    open: useCallback(() => dispatch(setOpenModal(ApplicationModal.SIGN_LOGIN)), [dispatch]),
    close: useCallback(() => dispatch(setOpenModal(null)), [dispatch])
  }
}
export function useSignLoginModalToggle(): () => void {
  return useToggleModal(ApplicationModal.SIGN_LOGIN)
}

// returns a function that allows adding a popup
export function useAddPopup(): (content: PopupContent, key?: string) => void {
  const dispatch = useDispatch()

  return useCallback(
    (content: PopupContent, key?: string) => {
      dispatch(addPopup({ content, key }))
    },
    [dispatch]
  )
}

// returns a function that allows removing a popup via its key
export function useRemovePopup(): (key: string) => void {
  const dispatch = useDispatch()
  return useCallback(
    (key: string) => {
      dispatch(removePopup({ key }))
    },
    [dispatch]
  )
}

// get the list of active popups
export function useActivePopups(): AppState['application']['popupList'] {
  const list = useSelector((state: AppState) => state.application.popupList)
  return useMemo(() => list.filter(item => item.show), [list])
}

export function useUserLocation(): AppState['application']['userLocation'] {
  return useSelector((state: AppState) => state.application.userLocation)
}

export function useDaoInfoLeftSidedOpenStatus() {
  const dispatch = useDispatch<AppDispatch>()
  return useCallback((open: boolean) => dispatch(setDaoInfoLeftSidedOpenStatus(open)), [dispatch])
}
